import type { SlideDeck } from './slideData';
import { BAND1_MONTHLY, BAND1_ANNUAL, ONBOARDING_BASE, MIN_USERS } from '../lib/pricingConstants';

const customCSS = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');

.dark-theme .reveal { font-family: 'DM Sans', sans-serif; }
.dark-theme .reveal h1, .dark-theme .reveal h2 {
	font-family: 'DM Sans', sans-serif;
	color: #e8e8f0;
	text-transform: none;
	letter-spacing: -0.02em;
	font-weight: 700;
}
.dark-theme .reveal h1 { font-size: 2.6em; line-height: 1.1; }
.dark-theme .reveal h2 { font-size: 1.8em; line-height: 1.2; margin-bottom: 0.6em; }
.dark-theme .reveal h3 { font-size: 1.16em; line-height: 1.3; color: #E8491C; font-weight: 600; font-family: 'DM Sans', sans-serif; }
.dark-theme .reveal p, .dark-theme .reveal li { font-size: 0.72em; line-height: 1.6; color: #f0f0f5; }
.dark-theme .reveal ul { list-style: none; padding-left: 0; }
.dark-theme .reveal ul li { padding: 0.3em 0 0.3em 1.4em; position: relative; }
.dark-theme .reveal ul li::before { content: ''; position: absolute; left: 0; top: 0.75em; width: 6px; height: 6px; border-radius: 50%; background: #E8491C; }
.dark-theme .reveal .slides section { padding: 40px 60px; text-align: left; }

.regulatory-accent { width: 60px; height: 4px; background: #E8491C; border-radius: 2px; margin-bottom: 1.2em; }

.regulatory-bq { background: rgba(255,255,255,0.04); border-left: 4px solid #E8491C; padding: 0.8em 1.2em; margin: 0.8em 0; border-radius: 0 8px 8px 0; width: 90%; box-shadow: none; }
.regulatory-bq p { color: #f0f0f5 !important; font-size: 0.78em !important; font-weight: 400; }

.regulatory-section { text-align: center !important; }
.regulatory-section-num { font-family: 'JetBrains Mono', monospace; font-size: 0.6em; color: #E8491C; font-weight: 500; margin-bottom: 0.4em; display: block; }

.regulatory-icon { display: inline-flex; align-items: center; justify-content: center; width: 44px; height: 44px; border-radius: 10px; font-size: 1.2em; margin-bottom: 12px; }
.regulatory-icon-orange { background: rgba(232,73,28,0.15); }
.regulatory-icon-blue { background: rgba(59,130,246,0.15); }
.regulatory-icon-green { background: rgba(34,197,94,0.15); }
.regulatory-icon-amber { background: rgba(245,158,11,0.15); }

.regulatory-card-row { display: flex; gap: 20px; margin-top: 0.8em; }
.regulatory-card { flex: 1; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.06); border-radius: 12px; padding: 24px; }
.regulatory-card h3 { margin-top: 0; margin-bottom: 0.3em; font-size: 1em; }
.regulatory-card .card-q { font-size: 0.65em; color: #9898b0; font-style: italic; margin-bottom: 0.6em; }
.regulatory-card p, .regulatory-card li { font-size: 0.62em; }

.regulatory-pricing-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-top: 0.8em; }
.regulatory-price-card { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.06); border-radius: 12px; padding: 24px; text-align: center; }
.regulatory-price-card.highlight { border-color: #E8491C; background: rgba(232,73,28,0.06); }
.regulatory-price-card .label { font-size: 0.6em; color: #9898b0; text-transform: uppercase; letter-spacing: 0.08em; font-weight: 600; }
.regulatory-price-card .amount { font-size: 1.8em; font-weight: 700; color: #e8e8f0; margin: 0.2em 0; }
.regulatory-price-card .amount .currency { font-size: 0.6em; vertical-align: top; }
.regulatory-price-card .detail { font-size: 0.55em; color: #9898b0; }

.regulatory-big-stat { font-size: 1.95em; font-weight: 700; color: #E8491C; line-height: 1; margin: 0.2em 0; }

.regulatory-flow { display: flex; align-items: center; gap: 0; margin-top: 1em; justify-content: center; }
.regulatory-flow-step { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 10px; padding: 16px 20px; text-align: center; min-width: 140px; }
.regulatory-flow-step .name { font-size: 0.7em; font-weight: 600; color: #e8e8f0; }
.regulatory-flow-step .desc { font-size: 0.52em; color: #9898b0; margin-top: 4px; }
.regulatory-flow-arrow { font-size: 1.2em; color: #E8491C; padding: 0 8px; }

.regulatory-demo-badge { display: inline-block; background: #E8491C; color: white; font-size: 0.5em; font-weight: 600; padding: 4px 14px; border-radius: 20px; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 0.6em; }

.regulatory-cta { background: linear-gradient(135deg, rgba(232,73,28,0.1), rgba(232,73,28,0.03)); border: 1px solid rgba(232,73,28,0.2); border-radius: 16px; padding: 32px 40px; margin-top: 1em; }
.regulatory-cta p { color: #f0f0f5 !important; font-size: 0.78em !important; }

.regulatory-compact li { padding: 0.15em 0 0.15em 1.4em !important; }
.regulatory-compact li::before { top: 0.55em !important; }

.our-source-grid { display: flex; gap: 16px; margin-top: 0.8em; }
.our-source-card { flex: 1; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.06); border-radius: 12px; padding: 20px; }
.our-source-card .src-num { font-family: 'JetBrains Mono', monospace; font-size: 0.6em; color: #E8491C; font-weight: 600; }
.our-source-card h3 { margin: 0.3em 0; font-size: 0.92em; }
.our-source-card p { font-size: 0.6em !important; color: #c8c8d4; }

/* Hurricane Melissa — full-slide satellite background; scrim keeps text legible */
.melissa-stage { position: relative; z-index: 2; }
.melissa-stage h2,
.melissa-stage p,
.melissa-stage li { text-shadow: 0 1px 14px rgba(0,0,0,0.8), 0 0 3px rgba(0,0,0,0.65); }

/* Livewire side-logo (bottom-left): render white and a touch larger over the dark deck */
#side { width: 150px !important; }
#side svg { filter: brightness(0) invert(1); }

/* Executive summary — quad with vertical spine labels + headings (hh) */
.quad-grid { display:grid; grid-template-columns:1fr 1fr; grid-template-rows:1fr 1fr; gap:14px; margin-top:0.6em; align-items:stretch; }
.quad-grid .regulatory-card { margin:0; padding:18px; }
.quad-vcardB { padding:0 !important; display:flex; overflow:hidden; }
.quad-vcardB .qb-spine { flex:0 0 auto; display:flex; align-items:center; justify-content:center; padding:0 8px; border-left:1px solid rgba(232,73,28,0.45); writing-mode:vertical-rl; transform:rotate(180deg); font-size:0.6em; font-weight:700; letter-spacing:0.16em; color:#E8491C; text-transform:uppercase; }
.quad-vcardB .qb-body { padding:18px 20px; }
`;

// Derived pricing for slides (5-8 analysts, monthly, no annual discount)
const saasLow = (5 * BAND1_MONTHLY * 12).toLocaleString();
const saasHigh = (8 * BAND1_MONTHLY * 12).toLocaleString();
const yearOneLow = (5 * BAND1_MONTHLY * 12 + ONBOARDING_BASE).toLocaleString();
const yearOneHigh = (8 * BAND1_MONTHLY * 12 + ONBOARDING_BASE).toLocaleString();
const onboardingDisplay = ONBOARDING_BASE.toLocaleString();
const minMonthly = (MIN_USERS * BAND1_MONTHLY).toLocaleString();

export const livewireMinistryPresentation: SlideDeck = {
	slug: 'livewire-ministry-of-energy-transport-and-telecommunications',
	name: 'Ministry Presentation',
	subtitle: 'Independent network intelligence for Jamaica\'s electricity sector',
	color: '#1a1a2e',
	darkTheme: true,
	customCSS,
	slides: [
		// 1 — Title
		{
			title: '',
			background: '#1a1a2e',
			notes: `<p><i>[Let it sit a beat.]</i></p><p>"Thank you for the time. I'm Rhys Stewart, from Auchindown. I want to show you Livewire, and really, what it looks like when the people responsible for the electricity network have an independent view of it, rather than seeing it only through the utility. I'll keep it concrete, and I'd rather this be a conversation than a monologue, so stop me anywhere."</p><p><i>[The open is deliberately neutral, "the people responsible," not "the OUR" or "the ministry." You don't pick a buyer yet. You're about to find out which one they are.]</i></p>`,
			content: `
				<h1 style="font-size:3em;margin-bottom:0.1em;font-family:lvwr-ubuntu-thin,sans-serif;">
					<span style="color:#E8491C;font-weight:700;">Livewire</span>
				</h1>
				<p style="font-size:1.1em;color:#f0f0f5;font-weight:400;margin-top:0.3em;margin-bottom:0.6em;">
					Independent Network Intelligence for Jamaica&rsquo;s Electricity Sector
				</p>
				<div>
					<p style="font-size:0.6em;color:#9898b0;font-weight:500;letter-spacing:0.05em;text-transform:uppercase;">
					Prepared for the Ministry of Energy, Transport &amp; Telecommunications
					</p>
				</div>
				<div style="font-size:0.6em;color:#f0f0f5;border-top:1px solid rgba(255,255,255,0.08);padding-top:1em;margin-top:1.5em;">
					<br>Rhys Stewart <br> Auchindown<br>
				</div>`,
		},
		// 1b — Executive summary (hh)
		{
			title: '',
			background: '#1a1a2e',
			notes: `<p><i>[Optional. Either advance straight past it, or use it as a 20-second roadmap. If you roadmap it:]</i></p><p>"In one view, here's the whole argument. A problem: the network is known only through the entity that operates it. A solution: an independent record you hold yourselves. The value: moving from assurances to evidence. And a small, low-risk way to start. I'll walk each one."</p><p><i>[Do NOT say "real-time" here even if the card still does, fix the card.]</i></p>`,
			content: `
				<div class="regulatory-accent"></div>
				<h2>Executive summary</h2>
				<div class="quad-grid">
					<div class="regulatory-card quad-vcardB" style="grid-column:1;grid-row:1;"><div class="qb-spine">Problem</div><div class="qb-body"><h3>A borrowed picture</h3><p>The network is known only through the entity that operates it. In a rate case or after a storm, verifying it means taking that picture on trust.</p></div></div>
					<div class="regulatory-card quad-vcardB" style="grid-column:2;grid-row:1;"><div class="qb-spine">Solution</div><div class="qb-body"><h3>An independent view</h3><p>A queryable record of the network, held and controlled by you. Browser-based, nothing to install.</p></div></div>
					<div class="regulatory-card quad-vcardB" style="grid-column:1;grid-row:2;"><div class="qb-spine">Value</div><div class="qb-body"><h3>Assurances to evidence</h3><p>Rate cases grounded in evidence. Restoration you can verify after a storm. Conclusions that hold up because the data is yours.</p></div></div>
					<div class="regulatory-card quad-vcardB" style="grid-column:2;grid-row:2;"><div class="qb-spine">The Ask</div><div class="qb-body"><h3>A low-risk pilot</h3><p>A segment of the network, maybe a few feeders, in your own secure space, evaluated on your terms.</p></div></div>
				</div>`,
		},
		// 2 — Section 01
		{
			title: '',
			background: '#1a1a2e',
			notes: `<p>"Let me start with the problem this exists to solve. It's structural, and it's about information."</p>`,
			content: `
				<div class="regulatory-section">
					<span class="regulatory-section-num">01</span>
					<h2 style="font-size:2.2em;">The Challenge</h2>
					<p style="max-width:600px;margin:0 auto;">Information asymmetry in the electricity sector</p>
				</div>`,
		},
		// 3 — The problem
		{
			title: '',
			background: '#1a1a2e',
			notes: `<p>"Every high-stakes decision about this network rests on data that flows through the entity being regulated."</p><p><i>[advance]</i> "A rate case. A service-standard ruling. The terms of a new licence. All of it leans on what JPS provides, in Jamaica, that's the reality."</p><p><i>[advance]</i> "And I want to be careful: that's not a criticism of JPS. It's structural. Oversight depends on the regulated, not just for the grid data, but for the analysis of it."</p><p><i>[advance]</i> "What tends to arrive are summaries and reports. The methods behind them usually stay a black box."</p><p><i>[advance]</i> "And it bites in two places at once, in the day-to-day oversight of JPS, and in the once-in-a-generation framework reform now underway."</p><p><i>[Discovery cue. This slide names both worlds on purpose. Drop one open question and let them tell you which they care about:]</i> "As you think about where a tool like this sits, do you picture it with the regulator, running oversight day to day, or here in the ministry, feeding the reform work directly?"</p><p><i>[Then listen. Their answer steers your close.]</i></p>`,
			content: `
				<div class="regulatory-accent"></div>
				<h2>The network is visible only through the entity that operates it</h2>
				<ul>
					<li>Every high-stakes decision, a rate case, a service-standard ruling, the terms of a new licence, rests on data that flows through the regulated entity. In Jamaica, that means JPS</li>
					<li>Regulator and policymaker alike depend on the regulated to share not only grid information, but also the analysis of it</li>
					<li>What's provided are summaries and reports. The underlying methods are usually a black box</li>
					<li>It bites in two places at once: the day-to-day oversight of JPS, and the once-in-a-generation framework reform now underway</li>
				</ul>`,
		},
		// 4 — Hurricane Melissa (explicit)

		{
			title: '',
			background: '#1a1a2e',
			backgroundVideo: '/hurricane-melissa-bg.webm',
			notes: `<p><i>[Heavy slide. Slow down.]</i></p><p>"Melissa made this concrete."</p><p><i>[advance]</i> "In the aftermath, you needed to verify, the true scope of damage; whether restoration was progressing as reported, parish by parish; and what had actually changed on the ground versus what was filed."</p><p><i>[advance — the blockquote. Narrate the transformer scene; look at them, not the screen:]</i> "Take a real example. JPS reports they replaced the transformers along a stretch of road and restored service. On the screen, that's the question, <b>could you check it?</b> With Livewire you can. You hold an independent baseline of what was there before the storm, and you can send someone to drop a geolocated, timestamped annotation right at that pole. The claim stops being something you accept, and becomes something you confirm."</p><p><i>[advance — orange line]</i> "An independent baseline before. Field verification after. Answers you confirm, not just receive."</p><p><i>[Never say "real-time" or "live during the storm." Your view is as current as the last submission; the strength is the baseline and the field check, not a live feed.]</i></p>`,
			content: `
				<div class="melissa-stage">
					<div class="regulatory-accent"></div>
					<h2>Hurricane Melissa showed us the gap</h2>
					<p style="font-size:0.85em;max-width:720px;">
						In the aftermath, you needed to verify:
					</p>
					<ul>
						<li>The true scope and extent of damage across the network</li>
						<li>Whether restoration was progressing as reported, parish by parish</li>
						<li>What had actually changed on the ground versus what was filed</li>
					</ul>
					<div class="regulatory-bq">
						<p>How much of that picture could you check, rather than take on JPS's word?</p>
					</div>
					<p style="margin-top:1.2em;font-size:0.85em;color:#E8491C;font-weight:600;">
						With an independent baseline before the storm and field verification after, the answers are yours to confirm, not just to receive.
					</p>
				</div>`,
		},
		// 5 — Section 02
		{
			title: '',
			background: '#1a1a2e',
			notes: `<p>"So what is it. In one line: a spatial digital twin, built for regulatory intelligence."</p><p><i>[Credibility beat, your strongest asset, and it's not on a slide:]</i> "And I'm not coming at this from outside. I spent close to two decades inside JPS as a GIS analyst, doing exactly this kind of network-data work. Livewire is, in a sense, the tool I always wished sat on the other side of the table."</p>`,
			content: `
				<div class="regulatory-section">
					<span class="regulatory-section-num">02</span>
					<h2 style="font-size:2.2em;">What is <span style="font-family:lvwr-ubuntu-thin,sans-serif;color:#E8491C;">Livewire</span>?</h2>
					<p style="max-width:600px;margin:0 auto;">A spatial digital twin for regulatory intelligence</p>
				</div>`,
		},
		// 6 — Digital twin
		{
			title: '',
			background: '#1a1a2e',
			notes: `<p>"At its core, three things,"</p><p><i>[advance]</i> "seeing, tracking, and analyzing the state of the network, independently."</p><p><i>[advance]</i> "It's not an OMS, and not an ADMS. It doesn't replace or compete with the tools JPS uses to <b>run</b> the grid. Different job."</p><p><i>[advance]</i> "Browser-based, no GIS software, nothing to install."</p><p><i>[advance]</i> "And mobile-ready, inspect an asset and record what you find from a phone, standing in front of it."</p><p><i>[advance — quote]</i> "JPS has tools for <b>operating</b> the network. Livewire gives you a tool for <b>overseeing</b> it."</p>`,
			content: `
				<div class="regulatory-accent"></div>
				<h2>A cloud-based spatial digital twin</h2>
				<ul>
					<li>A platform for <strong style="color:#e8e8f0;">seeing, tracking, and analyzing</strong> network state <u>independently</u></li>
					<li>It does not replace or compete with the utility's operational tools</li>
					<li>Browser-based, with no GIS software or desktop installation</li>
					<li>Mobile ready. Inspect assets and annotate findings directly from a phone or tablet in the field</li>
				</ul>
				<div class="regulatory-bq" style="margin-top:1em;">
					<p>JPS has tools for <em>operating</em> the network.<br>
					Livewire gives you a tool for <em>overseeing</em> it.</p>
				</div>`,
		},
		// 7 — Data provenance (NEW) — "Where does the data come from?"
		{
			title: '',
			background: '#1a1a2e',
			notes: `<p><i>[Invite the objection, it's the right one.]</i></p><p>"The first question a technical person asks is the right one: where does the data come from? Three sources."</p><p><i>[advance]</i> "One, utility submissions. Whatever JPS files under the framework, Livewire structures into something you can query. And you set the format it arrives in, so it stops being a black box."</p><p><i>[advance]</i> "Two, your own field collection. Staff recording findings directly. Independent ground truth, captured by you."</p><p><i>[advance]</i> "Three, open and public sources. Imagery, boundaries, public infrastructure data."</p><p><i>[advance — quote]</i> "Independence was never about excluding JPS's data. It's about what happens to it. Once it lives in your own versioned, timestamped, queryable system, you can verify it, trace its history, and check it against the ground."</p><p><i>If pressed, "is JPS required to file this data?":</i> "You'd know the licence conditions far better than I would, and with the framework being rewritten, that's a question you're in a position to answer. My point is narrower: whatever you receive, and whatever you collect yourselves, you should be able to hold and verify."</p>`,
			content: `
				<div class="regulatory-accent"></div>
				<h2>"But where does the data come from?"</h2>
				<div class="our-source-grid">
					<div class="our-source-card">
						<span class="src-num">01</span>
						<h3>Utility submissions</h3>
						<p>Whatever JPS files under the regulatory framework, Livewire structures into a queryable model. And you set the format it arrives in.</p>
					</div>
					<div class="our-source-card">
						<span class="src-num">02</span>
						<h3>Your own field collection</h3>
						<p>Staff inspect assets and record findings directly. Independent ground truth, captured in the field.</p>
					</div>
					<div class="our-source-card">
						<span class="src-num">03</span>
						<h3>Open &amp; public sources</h3>
						<p>Imagery, parish and administrative boundaries, and publicly available infrastructure data.</p>
					</div>
				</div>
				<div class="regulatory-bq" style="margin-top:1em;">
					<p>Independence isn't about excluding JPS's data. It's about what happens to it. Once it lives in your own versioned, timestamped, queryable system, you can verify it, trace its history, and check it against the ground.</p>
				</div>`,
		},
		// 8 — Section 03
		{
			title: '',
			background: '#1a1a2e',
			notes: `<p>"That's the foundation. Here's what you do with it, six capabilities that form one framework. I'll come back to how they connect."</p><p><i>[If pacing is tight, move briskly here and let the demo carry the depth.]</i></p>`,
			content: `
				<div class="regulatory-section">
					<span class="regulatory-section-num">03</span>
					<h2 style="font-size:2.2em;">The Regulator's Toolkit</h2>
					<p style="max-width:600px;margin:0 auto;">Six capabilities. One oversight framework.</p>
				</div>`,
		},
		// 9 — Spotlight
		{
			title: '',
			background: '#1a1a2e',
			notes: `<p>"Spotlight answers 'what's there?' Draw a boundary around any area, a parish, a feeder zone, a community, and instantly see everything inside."</p><p><i>[advance]</i> "Asset counts, types, conditions, connectivity, in seconds."</p><p><i>[advance]</i> "No request to JPS, no waiting on a report."</p><p><i>[advance]</i> "Useful for consumer complaints, checking service equity across parishes, any quick investigation."</p>`,
			content: `
				<h3>Spotlight — "What's there?"</h3>
				<p style="font-size:0.78em;margin-bottom:0.6em;">
					Draw a boundary around any area (a parish, a feeder zone, a community) and instantly see what's inside.
				</p>
				<ul>
					<li>Asset counts, types, conditions, connectivity, all in seconds</li>
					<li>No request to JPS. No waiting for a report.</li>
				</ul>
				<p style="margin-top:0.8em;font-size:0.65em;">
					<strong style="color:#E8491C;">Regulatory use:</strong> Consumer complaints, service equity across parishes, ad-hoc investigations
				</p>`,
		},
		// 10 — Tracing
		{
			title: '',
			background: '#1a1a2e',
			notes: `<p>"Tracing answers 'what's connected?' Pick any point and trace upstream to the source or downstream to every customer, by phase."</p><p><i>[advance]</i> "How many customers sit downstream of an aging switch?"</p><p><i>[advance]</i> "Is this feeder balanced, or is load piled on one phase?"</p><p><i>[advance]</i> "What's the full path from substation to a specific transformer?"</p><p><i>[advance]</i> "For assessing the real impact of a failure, checking phase-balance claims, seeing where the network is exposed."</p>`,
			content: `
				<h3>Tracing — "What's connected?"</h3>
				<p style="font-size:0.78em;margin-bottom:0.6em;">
					Select any point on the network and trace upstream to the source or downstream to every connected customer, by phase.
				</p>
				<ul>
					<li>How many customers are downstream of an aging switch or conductor?</li>
					<li>Is this feeder balanced across phases, or is load concentrated on one?</li>
					<li>What's the full path from substation to a specific transformer?</li>
				</ul>
				<p style="margin-top:0.8em;font-size:0.65em;">
					<strong style="color:#E8491C;">Regulatory use:</strong> Assessing customer impact of infrastructure failures, verifying phase balance claims, understanding network exposure
				</p>`,
		},
		// 11 — Chronicle
		{
			title: '',
			background: '#1a1a2e',
			notes: `<p>"Chronicle answers 'what changed?' It tracks every change over time and lets you compare any two points in history."</p><p><i>[advance]</i> "Verify an upgrade actually happened, independently."</p><p><i>[advance]</i> "Or set a pre-storm baseline against post-storm reality, spatial evidence, not a damage report."</p><p><i>[advance]</i> "For rate-case verification, post-disaster assessment, tracking whether investment produced what was promised."</p>`,
			content: `
				<h3>Chronicle — "What changed?"</h3>
				<p style="font-size:0.78em;margin-bottom:0.6em;">
					Track every change to the network over time. Compare any two points in history.
				</p>
				<ul>
					<li>Verify infrastructure upgrades independently</li>
					<li>Pre-storm baseline vs. post-storm reality: spatial evidence, not damage reports</li>
				</ul>
				<p style="margin-top:0.8em;font-size:0.65em;">
					<strong style="color:#E8491C;">Regulatory use:</strong> Rate case verification, post-disaster assessment, infrastructure investment tracking
				</p>`,
		},
		// 12 — Annotations
		{
			title: '',
			background: '#1a1a2e',
			notes: `<p>"Annotations answers 'what did we find?' Send staff to the field, inspect, record findings directly."</p><p><i>[advance]</i> "Flag discrepancies, missing equipment, records that don't match, conditions nobody reported."</p><p><i>[advance]</i> "Photos and comments, geolocated and timestamped, tied to the asset."</p><p><i>[advance]</i> "This is the field verification behind the Melissa example, the transformer check, made routine."</p>`,
			content: `
				<h3>Annotations — "What did we find?"</h3>
				<p style="font-size:0.78em;margin-bottom:0.6em;">
					Send staff to the field. Inspect assets. Record findings directly in Livewire.
				</p>
				<ul>
					<li>Flag discrepancies: missing equipment, mismatched records, unreported conditions</li>
					<li>Photos, comments, geolocated and timestamped, tied to the specific asset</li>
				</ul>
				<p style="margin-top:0.8em;font-size:0.65em;">
					<strong style="color:#E8491C;">Regulatory use:</strong> Field verification, compliance audits, independent evidence for rate case reviews
				</p>`,
		},
		// 13 — Reliability Metrics (enhanced with JPS licence standards)
		{
			title: '',
			background: '#1a1a2e',
			notes: `<p>"Reliability answers 'how is JPS performing?' Livewire calculates SAIDI, SAIFI, and CAIDI independently, from the network model, not from a submitted report."</p><p><i>[advance]</i> "The indices come straight from topology and outage data."</p><p><i>[advance]</i> "Break them down by feeder, parish, any boundary you draw."</p><p><i>[advance]</i> "And compare JPS's reported numbers against the Guaranteed and Overall Standards in its licence."</p><p><i>[advance]</i> "For enforcing standards, benchmarking, rate-case evidence, and finding the areas that are quietly underserved."</p>`,
			content: `
				<h3>Reliability Metrics — "How is JPS performing?"</h3>
				<p style="font-size:0.78em;margin-bottom:0.6em;">
					Calculate SAIDI, SAIFI, and CAIDI independently, from the network model, not from utility-submitted reports.
				</p>
				<ul>
					<li>Derive reliability indices directly from network topology and outage data</li>
					<li>Break down performance by feeder, parish, or any spatial boundary</li>
					<li>Compare JPS's reported metrics against the Guaranteed and Overall Standards in its licence</li>
				</ul>
				<p style="margin-top:0.8em;font-size:0.65em;">
					<strong style="color:#E8491C;">Regulatory use:</strong> Enforcing service standards, performance benchmarking, rate case evidence, identifying underserved areas
				</p>`,
		},
		// 14 — Network Baseline
		{
			title: '',
			background: '#1a1a2e',
			notes: `<p>"And underneath it all sits the Baseline, your own record of network state. Independent, timestamped, versioned, queryable."</p><p><i>[advance — quote]</i> "When there's a dispute about network condition or investment, you're not comparing claims. You're comparing data."</p>`,
			content: `
				<h3>Network Baseline — "What's the truth?"</h3>
				<p style="font-size:0.78em;margin-bottom:0.6em;">
					Your own record of network state: independent, timestamped, versioned, and queryable.
				</p>
				<div class="regulatory-bq">
					<p>When there's a dispute about network condition or investment, you're not comparing claims. You're comparing data.</p>
				</div>`,
		},
		// 15 — Oversight loop
		{
			title: '',
			background: '#1a1a2e',
			notes: `<p>"Here's how the six connect. Spotlight, what should be there. Tracing, what's connected. Chronicle, what changed. Annotations, what's actually on the ground. Reliability, how they're performing. And it all resolves into one verified baseline, held by you."</p><p>"Independent. Continuous. Yours."</p>`,
			content: `
				<div class="regulatory-accent"></div>
				<h2>The oversight Framework</h2>
				<div class="regulatory-flow" style="justify-content:center;gap:4px;">
					<div class="regulatory-flow-step" style="min-width:0;padding:12px 10px;">
						<div class="name">Spotlight</div>
						<div class="desc">What should<br>be there?</div>
					</div>
					<div class="regulatory-flow-arrow">→</div>
					<div class="regulatory-flow-step" style="min-width:0;padding:12px 10px;">
						<div class="name">Tracing</div>
						<div class="desc">What's<br>connected?</div>
					</div>
					<div class="regulatory-flow-arrow">→</div>
					<div class="regulatory-flow-step" style="min-width:0;padding:12px 10px;">
						<div class="name">Chronicle</div>
						<div class="desc">What<br>changed?</div>
					</div>
					<div class="regulatory-flow-arrow">→</div>
					<div class="regulatory-flow-step" style="min-width:0;padding:12px 10px;">
						<div class="name">Annotations</div>
						<div class="desc">What's on<br>the ground?</div>
					</div>
					<div class="regulatory-flow-arrow">→</div>
					<div class="regulatory-flow-step" style="min-width:0;padding:12px 10px;">
						<div class="name">Reliability</div>
						<div class="desc">How are they<br>performing?</div>
					</div>
					<div class="regulatory-flow-arrow">→</div>
					<div class="regulatory-flow-step" style="min-width:0;padding:12px 10px;border-color:#E8491C;">
						<div class="name" style="color:#E8491C;">Baseline</div>
						<div class="desc">The verified<br>truth</div>
					</div>
				</div>
				<p style="text-align:center;margin-top:1.2em;font-size:0.72em;">
					Independent. Continuous. Yours.
				</p>`,
		},
		// 16 — Data governance (NEW)
		{
			title: '',
			background: '#1a1a2e',
			notes: `<p>"A word on the part you'll care about most, governance, because this is critical-infrastructure data."</p><p><i>[advance]</i> "A secure space of your own, isolated, never shared with the utility or anyone else."</p><p><i>[advance]</i> "Role-based access, you control exactly who on your team sees and edits what."</p><p><i>[advance]</i> "Cloud-hosted and encrypted, with nothing for you to stand up or maintain."</p><p><i>[advance]</i> "Critical-infrastructure data, handled with the security it warrants."</p><p><i>[advance — quote]</i> "The independence of oversight depends on the regulator holding the data. With Livewire, it does."</p><p><i>If asked "where is it hosted, is it offshore?":</i> "Fair question, and for critical national infrastructure I'd expect it. We can host in the jurisdiction you require, including in-country, encrypted throughout, with full access logging and a continuity plan so you're never locked out. I'd rather agree those terms with your team than dictate them."</p>`,
			content: `
				<div class="regulatory-accent"></div>
				<h2>Your data. Your secure space. Your control.</h2>
				<ul>
					<li><strong style="color:#e8e8f0;">A secure space dedicated to you</strong>, isolated and never shared with the utility or other tenants</li>
					<li><strong style="color:#e8e8f0;">Role-based access</strong>, so you control exactly who on your team sees and edits what</li>
					<li><strong style="color:#e8e8f0;">Cloud-hosted and encrypted</strong>, with no servers or GIS infrastructure for you to maintain</li>
					<li><strong style="color:#e8e8f0;">Critical-infrastructure data</strong> handled with the security it warrants</li>
				</ul>
				<div class="regulatory-bq" style="margin-top:1em;">
					<p>The independence of oversight depends on the regulator holding the data. With Livewire, it does.</p>
				</div>`,
		},
		// 17 — Section 04 Demo (framed as JPS stand-in)
		{
			title: '',
			background: '#0f0f1e',
			notes: `<p>"Enough slides, let me show you the real thing. This is a demonstration network, Everglades Electric. Completely fictional, so nothing here is anyone's real data."</p><p><i>[advance]</i> "A stand-in for today. In production, this would be the JPS network, in your own secure space."</p><p><i>[Lead with Spotlight, then Tracing, then Chronicle's before/after. Three that land hardest, don't re-narrate all six. Highest-risk moment; rehearse it cold.]</i></p>`,
			content: `
				<div class="regulatory-section">
					<span class="regulatory-section-num">04</span>
					<div class="regulatory-demo-badge">Live Demo</div>
					<h2 style="font-size:2.2em;">Everglades Electric</h2>
					<p style="max-width:640px;margin:0 auto;">A demonstration utility network: see what an independent view of the grid looks like in Livewire</p>
					<p style="max-width:640px;margin:0.8em auto 0;font-size:0.62em;color:#9898b0;">
						A stand-in for today. In production, this is the JPS network, in your own secure space.
					</p>
				</div>`,
		},
		// 18 — Section 05 The Ask / Pilot (softened for first contact)
		{
			title: '',
			background: '#1a1a2e',
			notes: `<p><i>[Soft. By now you've heard which way they lean, pivot the close accordingly.]</i></p><p>"So where could this go? I'm not asking you to commit to anything today. Let's put it to the test with a small pilot, a slice of the network, in your own secure space, for your team to evaluate hands-on. On your terms, at low commitment."</p><p><i>[Pivot the close on the discovery read:]</i></p><p><b>OUR-leaning:</b> "...so you can see what independent oversight does with real data, rate-case evidence, standards you can check, complaints you can investigate directly."</p><p><b>Ministry-leaning:</b> "...so there's an independent evidentiary base in hand while the new licence and framework are being designed. With the current licence expiring in 2027, there's a natural window to have this in place before the terms are set."</p><p><b>Still unclear:</b> "...and you decide where it leads, whether it lives with the regulator or here. The pilot doesn't require settling that today."</p>`,
			content: `
				<div class="regulatory-section" style="text-align:left !important;">
					<span class="regulatory-section-num" style="text-align:left;">05</span>
<h2>Proposed next steps</h2>
<div class="regulatory-cta">
  <p><strong style="color:#E8491C;font-size:1.1em;">Stop taking the picture on trust.</strong></p>
  <p style="margin-top:0.6em;">Start with a small section - a parish, half a dozen feeders - and we get you onboard. Two weeks to a secure, working independent view, on real data.</p>
  <p style="margin-top:0.6em;">Your team puts it against something live: a complaint, a restoration claim, a rate-case question. You see exactly what an independent view changes.</p>
  <p style="margin-top:0.6em;">A small first step, and then you decide based on the evidence, not the pitch.</p>
</div>					


				</div>`,
			verticalSlides: [
				{
					title: '',
					background: '#1a1a2e',
					notes: `<p><i>[Only if asked.]</i></p><p>"On cost, it's per-user, with a lower annual rate for larger teams. No GIS licensing, browser-based, storage included. I'm glad to shape it to however you actually procure software, and the pilot keeps the first step small."</p><p><i>[Be honest if pressed: there's a one-time onboarding on top of the subscription. Don't imply the monthly floor is the all-in cost.]</i></p>`,
					content: `
						<div class="regulatory-accent"></div>
						<h2>Simple, accessible pricing</h2>
												<div class="regulatory-card" style="text-align:center;border-color:rgba(232,73,28,0.3);background:rgba(232,73,28,0.06);">
							<p class="card-q">Starting price</p>
							<div class="regulatory-big-stat" style="font-size:3.2em;">$${minMonthly}<span style="font-size:0.28em;color:#9898b0;font-weight:600;"> / month</span></div>
							<p>Minimum ${MIN_USERS} users at $${BAND1_MONTHLY}/user, then scale up</p>
<p class="price-sub" style="font-size:0.62em;font-style:italic;color:#E89C84;margin-top:0.5em;padding-top:0.5em;border-top:1px solid rgba(255,255,255,0.06);display:inline-block;">+ one-time onboarding from $${onboardingDisplay.toLocaleString()} &middot; 2 weeks to go-live</p>
						</div>
						<p style="margin-top:0.9em;font-size:0.62em;text-align:center;">
							100 GB storage included · No GIS licensing required · Browser-based
						</p>`,
				},
				// Pricing — single block
				{
					title: '',
					background: '#1a1a2e',
					notes: `<p>"For a team that size, the annual figure on screen, plus a one-time onboarding to get you live in about two weeks. Year one lands below a single consultant engagement, except this doesn't leave when the consultant does. It's a capability you keep."</p>`,
					content: `
						<div class="regulatory-accent"></div>
						<h2>For 5 to 8 analysts</h2>
						<div class="regulatory-card-row">
							<div class="regulatory-card" style="text-align:center;">
								<p class="card-q">SaaS</p>
								<div class="regulatory-big-stat">$${saasLow} – $${saasHigh}</div>
								<p>per year</p>
							</div>
							<div class="regulatory-card" style="text-align:center;">
								<p class="card-q">Onboarding (one-time)</p>
								<div class="regulatory-big-stat">$${onboardingDisplay}+</div>
								<p>Jumpstart: 2 weeks to go-live</p>
							</div>
						</div>
						<div class="regulatory-card" style="margin-top:16px;text-align:center;border-color:rgba(232,73,28,0.2);">
							<p class="card-q">Year 1 all-in</p>
							<div class="regulatory-big-stat">$${yearOneLow} – $${yearOneHigh}</div>
							<p>Less than a single consultant engagement. Permanent, continuous oversight capability</p>
						</div>`,
				},
			],
		},
		// 19 — Closing
		{
			title: '',
			background: '#1a1a2e',
			notes: `<p>"That's Livewire, built to give whoever is responsible for this network an independent, continuous view of it. I'd value your read on it, and I'd like to talk about what a pilot could look like. Thank you."</p><p><i>[Stop. Let them respond. Don't fill the silence.]</i></p><hr><p><b>Q&amp;A, rehearse cold</b></p><p><b>Is JPS required to file this data?</b> You'd know the licence better than I would, and the framework's being rewritten now, so that's a question you can answer. My point is narrower: whatever you receive, and whatever you collect, you should be able to hold and verify.</p><p><b>Where is the data hosted? Offshore?</b> In the jurisdiction you require, including in-country. Encrypted throughout, access-logged, with a continuity plan so you're never locked out.</p><p><b>Does this give a live view during a crisis?</b> No, and I won't oversell it. Your view is as current as the last data you hold. The strength is the independent baseline before, and the field verification after, checking what you're told, not a live feed.</p><p><b>How is this not duplicating what JPS has?</b> JPS's tools operate the grid. This oversees it. Different purpose, different owner.</p><p><b>Who runs it day to day?</b> Browser-based, no GIS software, so the bar is low, but we also offer onboarding and can support training. Your team isn't on their own.</p><p><b>Has any other regulator done this?</b> Not yet, that's the opportunity. Jamaica could set the template the region follows.</p>`,
			content: `
				<div class="regulatory-section">
					<h1 style="margin-bottom:0.2em;font-size:3em;font-family:lvwr-ubuntu-thin,sans-serif;">
						<span style="color:#E8491C;">Livewire</span>
					</h1>
					<p style="font-size:0.85em;color:#9898b0;margin-bottom:1.5em;">An independent view of the grid</p>
					<p style="font-size:0.7em;color:#e8e8f0;">
						Rhys Stewart<br>
						<span style="color:#9898b0;">rhys@auchindown.com · lvwr.io</span>
					</p>
				</div>`,
		},
	],
};
